import { useEffect, useState } from "react";
import { AlertTriangle, LogOut, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/*
  Support Mode Banner — sticky bar shown while a support session is active.
  Backward compatible with the legacy impersonation keys.
*/
export function ImpersonationBanner() {
  const [target, setTarget] = useState(null);
  const [meta, setMeta] = useState({});
  const [duration, setDuration] = useState(0);
  const [ending, setEnding] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("zj_support_session_target")
             || sessionStorage.getItem("zj_impersonation_target");
    if (raw) {
      try { setTarget(JSON.parse(raw)); } catch { /* ignore */ }
    }
    setMeta({
      startedAt: sessionStorage.getItem("zj_support_session_started_at"),
      reason: sessionStorage.getItem("zj_support_session_reason"),
      startedBy: sessionStorage.getItem("zj_support_session_started_by"),
    });
  }, []);

  useEffect(() => {
    if (!meta.startedAt) return;
    const start = new Date(meta.startedAt).getTime();
    const t = setInterval(() => setDuration(Math.round((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(t);
  }, [meta.startedAt]);

  if (!target) return null;

  const end = async () => {
    setEnding(true);
    try {
      const return_token = sessionStorage.getItem("zj_return_token");
      const session_id = sessionStorage.getItem("zj_support_session_id")
                       || sessionStorage.getItem("zj_impersonation_id");
      // Try new endpoint first, fall back to legacy
      try {
        await api.post("/support-sessions/end", { return_token, session_id });
      } catch {
        await api.post("/customers/impersonate/end", { return_token, impersonation_id: session_id });
      }
      ["zj_return_token", "zj_support_session_id", "zj_support_session_target",
       "zj_support_session_started_at", "zj_support_session_reason", "zj_support_session_started_by",
       "zj_impersonation_id", "zj_impersonation_target"].forEach((k) => sessionStorage.removeItem(k));
      toast.success("Returned to Super Admin");
      window.location.href = "/customers";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to end support session");
      setEnding(false);
    }
  };

  const fmt = (s) => `${Math.floor(s / 60)}m ${s % 60}s`;

  return (
    <div
      data-testid="v2-support-mode-banner"
      style={{
        position: "sticky", top: 0, zIndex: 60,
        background: "linear-gradient(90deg, #F59E0B 0%, #C2410C 100%)",
        color: "#fff", padding: "10px 20px",
        display: "flex", alignItems: "center", gap: 12,
        fontSize: 13, fontWeight: 600,
        boxShadow: "0 4px 12px rgba(194, 65, 12, 0.4)",
      }}
    >
      <AlertTriangle size={16} />
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
        <span data-testid="v2-support-mode-label">
          SUPPORT MODE — Logged in as <b>{target.company_name}</b>
        </span>
        <span style={{ fontSize: 10.5, opacity: 0.9, fontWeight: 500 }}>
          {meta.startedBy && <>By {meta.startedBy} · </>}
          {meta.reason && <>Reason: {meta.reason.replaceAll("_", " ")} · </>}
          <Clock size={9} style={{ display: "inline", verticalAlign: "middle", marginRight: 3 }} />
          {fmt(duration)}
        </span>
      </div>
      <div style={{ flex: 1 }} />
      <button
        onClick={end}
        disabled={ending}
        data-testid="v2-support-mode-end"
        style={{
          background: "rgba(15, 15, 15, 0.85)", color: "#F59E0B",
          padding: "8px 14px", borderRadius: 6, border: 0,
          fontSize: 12, fontWeight: 700, cursor: "pointer",
          display: "flex", alignItems: "center", gap: 6,
        }}
      >
        <LogOut size={12} /> {ending ? "Ending…" : "Return to Admin"}
      </button>
    </div>
  );
}
